import { useCallback, useEffect, useState } from 'react';
import {
  $isListItemNode,
  $isListNode,
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  REMOVE_LIST_COMMAND,
} from '@lexical/list';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { mergeRegister } from '@lexical/utils';
import {
  $getSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_LOW,
  SELECTION_CHANGE_COMMAND,
} from 'lexical';
import { useTranslation } from 'react-i18next';

import ListOlIcon from 'assets/images/icons/list-ol.svg?react';
import ListUlIcon from 'assets/images/icons/list-ul.svg?react';
import Button from 'components/button/Button';
import { RichTextEditorToolbarPluginProps } from 'components/editor/RichTextEditor.types';

interface ListButtonsProps {
  disabled: boolean;
  toolbarSettings: RichTextEditorToolbarPluginProps['toolbarSettings'];
}

const ListButtons = ({
  disabled,
  toolbarSettings: { features },
}: ListButtonsProps) => {
  const { t } = useTranslation();
  const [editor] = useLexicalComposerContext();
  const [activeListType, setActiveListType] = useState<'ul' | 'ol' | null>(
    null,
  );

  const $updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      const anchorNode = selection.anchor.getNode();
      const immediateParent = anchorNode.getParent();
      const parent = $isListItemNode(immediateParent)
        ? immediateParent.getParent()
        : immediateParent;

      if ($isListNode(parent)) {
        setActiveListType(parent.getTag() as 'ul' | 'ol');
      } else {
        setActiveListType(null);
      }
    }
  }, []);

  const handleToggleList = useCallback(
    (type: 'ul' | 'ol') => {
      if (activeListType === type) {
        editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
      } else {
        editor.dispatchCommand(
          type === 'ul'
            ? INSERT_UNORDERED_LIST_COMMAND
            : INSERT_ORDERED_LIST_COMMAND,
          undefined,
        );
      }
    },
    [editor, activeListType],
  );

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          $updateToolbar();
        });
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        (_payload, _newEditor) => {
          $updateToolbar();
          return false;
        },
        COMMAND_PRIORITY_LOW,
      ),
    );
  }, [editor, $updateToolbar]);

  if (!features?.lists) {
    return null;
  }

  return (
    <>
      <Button
        aria-label={t('form.editor.buttons.orderedList')}
        className={`toolbar-item ${activeListType === 'ol' ? 'active' : ''}`}
        data-testid="toolbar-button-ol"
        disabled={disabled}
        onClick={() => handleToggleList('ol')}
      >
        <ListOlIcon />
      </Button>
      <Button
        aria-label={t('form.editor.buttons.unorderedList')}
        className={`toolbar-item ${activeListType === 'ul' ? 'active' : ''}`}
        data-testid="toolbar-button-ul"
        disabled={disabled}
        onClick={() => handleToggleList('ul')}
      >
        <ListUlIcon />
      </Button>
    </>
  );
};

export default ListButtons;
